/**
 * Menu-button icons: the "watch an ad" clapper (270×291, shown height-fixed in coral and green
 * buttons), the Daily calendar and the Wheel (256×256, 34 px on the blue retention buttons),
 * and the Shrink-aim cursor (32×32 at scale 1). Transparent frames: the buttons bring their
 * own plates, so each icon carries its own ink edge and a hard ink drop to sit on them.
 */
import { TONES } from "../palette.js";
import { celShape, glint, inkStyle } from "../toon-kit.js";
import {
  ICON_LINE, ICON_DETAIL, TAU, INK, celBandsY, inkedRope, inkSparkle, roundRectPath, roundStarPath,
  withInkDrop,
} from "./icon-kit.js";

var NAVY = { base: "#2f5384", light: "#5682bb", shade: "#1d375c" };
var PLAY = { base: "#fff8e8", light: "#ffffff", shade: "#c9def0" };
var GOLD = TONES.gold;

// ---- Ad (clapperboard) ------------------------------------------------------------------

/** A striped clapper stick: rounded bar with slanted white bands, in its own frame. */
function clapperStick(c, x, y, w, h, phase) {
  var r = h * 0.28;
  c.save();
  c.beginPath();
  roundRectPath(c, x, y, w, h, r);
  c.fillStyle = NAVY.base;
  c.fill();
  c.clip();
  var step = w / 4.2;
  c.fillStyle = "#ffffff";
  for (var i = -1; i < 6; i++) {
    var sx = x + i * step + phase * step;
    c.beginPath();
    c.moveTo(sx, y + h);
    c.lineTo(sx + step * 0.5, y + h);
    c.lineTo(sx + step * 0.5 + h * 0.75, y);
    c.lineTo(sx + h * 0.75, y);
    c.closePath();
    c.fill();
  }
  // Shade along the stick's foot.
  c.fillStyle = "rgba(23,42,58,0.22)";
  c.fillRect(x, y + h * 0.7, w, h * 0.3);
  c.restore();
  c.beginPath();
  roundRectPath(c, x, y, w, h, r);
  inkStyle(c, ICON_LINE * 0.9);
  c.stroke();
}

function paintAd(c, w, h) {
  var ink = ICON_LINE * 1.02;
  var bx = 20, bw = w - 40, by = 132, bh = h - 132 - 22;
  // The open clapper stick, hinged on the left.
  c.save();
  c.translate(bx + 10, by - 26);
  c.rotate(-0.27);
  clapperStick(c, -6, -34, bw + 4, 38, 0.35);
  c.restore();
  // The board.
  celShape(c, function (cc, g) { roundRectPath(cc, bx - g, by - 30 - g, bw + g * 2, bh + 30 + g * 2, 26 + g); },
    NAVY, 90, {
      line: ink,
      lightPath: function (cc) { roundRectPath(cc, bx - 20, by + 10, 34, bh - 18, 14); },
    });
  // The fixed stick along the board's top.
  clapperStick(c, bx, by - 30, bw, 38, 0);
  // Hinge pin.
  c.beginPath();
  c.arc(bx + 14, by - 28, 13, 0, TAU);
  c.fillStyle = GOLD.base;
  c.fill();
  inkStyle(c, ICON_DETAIL);
  c.stroke();
  glint(c, bx + 10, by - 32, 3.4);
  // Play triangle, rounded and cel shaded.
  var cx = bx + bw * 0.52, cy = by + 8 + (bh - 8) * 0.5;
  var R = 52;
  celShape(c, function (cc, g) { playPath(cc, cx, cy, R + g); }, PLAY, R, { line: ICON_DETAIL * 1.45 });
  // Glints on the board.
  c.fillStyle = "rgba(255,255,255,0.55)";
  c.beginPath();
  c.arc(bx + bw - 24, by + bh - 26, 6, 0, TAU);
  c.fill();
  inkSparkle(c, 34, 46, 17, GOLD.light, { line: 4 });
}

/** A rounded play triangle pointing right, centred on (x, y), circumradius R. */
function playPath(c, x, y, R) {
  var pts = [];
  for (var i = 0; i < 3; i++) {
    var a = (i / 3) * TAU;
    pts.push([x + Math.cos(a) * R + R * 0.1, y + Math.sin(a) * R]);
  }
  var k = 0.22;
  for (var j = 0; j < 3; j++) {
    var p = pts[j], n = pts[(j + 1) % 3], q = pts[(j + 2) % 3];
    var a0 = [p[0] + (q[0] - p[0]) * k, p[1] + (q[1] - p[1]) * k];
    var a1 = [p[0] + (n[0] - p[0]) * k, p[1] + (n[1] - p[1]) * k];
    if (j === 0) c.moveTo(a0[0], a0[1]);
    else c.lineTo(a0[0], a0[1]);
    c.quadraticCurveTo(p[0], p[1], a1[0], a1[1]);
  }
  c.closePath();
}

/** "Watch an ad": a navy clapperboard, its stick snapped open, a big play button on it. */
export function drawAdIcon(c, w, h) {
  withInkDrop(c, w, h, 6, function (cc) { paintAd(cc, w, h); });
}

// ---- Daily (calendar) -------------------------------------------------------------------

var PAGE = { base: "#fff8e8", light: "#ffffff", shade: "#efdcb8" };
var HEADER = { base: "#ff5a5f", light: "#ff9b9b", shade: "#d2363f" };
var STAR = { base: "#ffc84a", light: "#fff3b8", shade: "#e08a1c" };

function paintDaily(c) {
  c.save();
  c.translate(128, 136);
  c.rotate(-0.07);
  var x = -98, y = -88, w = 196, h = 182, r = 30;
  // Page (cream) with the header (red) on top, one silhouette.
  celShape(c, function (cc, g) { roundRectPath(cc, x - g, y - g, w + g * 2, h + g * 2, r + g); }, PAGE, 90, {
    line: ICON_LINE,
  });
  c.save();
  c.beginPath();
  roundRectPath(c, x, y, w, h, r);
  c.clip();
  c.fillStyle = celBandsY(c, y, y + 62, HEADER, { lit: 0.22, dark: 0.82 });
  c.fillRect(x - 4, y - 4, w + 8, 66);
  c.beginPath();
  c.moveTo(x - 4, y + 62);
  c.lineTo(x + w + 4, y + 62);
  inkStyle(c, ICON_DETAIL * 1.3);
  c.stroke();
  // Day squares, two rows (the star sits over the middle of them).
  c.fillStyle = "#bfe4f7";
  for (var row = 0; row < 2; row++) {
    for (var col = 0; col < 4; col++) {
      if (row === 1 && col === 3) continue;
      var sx = x + 22 + col * 41, sy = y + 82 + row * 42;
      c.beginPath();
      roundRectPath(c, sx, sy, 30, 28, 8);
      c.fill();
    }
  }
  c.restore();
  c.beginPath();
  roundRectPath(c, x, y, w, h, r);
  inkStyle(c, ICON_LINE);
  c.stroke();
  // Binder rings poking out of the header.
  for (var i = -1; i <= 1; i += 2) {
    var rx = i * 52;
    c.beginPath();
    c.arc(rx, y + 24, 9, 0, TAU);
    c.fillStyle = INK;
    c.fill();
    c.beginPath();
    c.moveTo(rx, y - 26);
    c.lineTo(rx, y + 22);
    inkedRope(c, 12, "#e8eef4", 5.5);
    c.beginPath();
    c.moveTo(rx - 2.5, y - 24);
    c.lineTo(rx - 2.5, y - 8);
    c.strokeStyle = "#ffffff";
    c.lineWidth = 4;
    c.stroke();
  }
  // The reward star, smiling.
  c.save();
  c.translate(26, 30);
  c.rotate(0.12);
  celShape(c, function (cc, g) { roundStarPath(cc, 0, 4, 62 + g, 33 + g, 0); }, STAR, 56, {
    line: ICON_LINE * 0.92,
    lightPath: function (cc) { roundStarPath(cc, -9, -6, 50, 26, 0); },
  });
  c.fillStyle = INK;
  c.beginPath();
  c.ellipse(-12, 4, 5.4, 7.5, 0, 0, TAU);
  c.ellipse(12, 4, 5.4, 7.5, 0, 0, TAU);
  c.fill();
  c.fillStyle = "#ffffff";
  c.beginPath();
  c.arc(-13.5, 1, 2.2, 0, TAU);
  c.arc(10.5, 1, 2.2, 0, TAU);
  c.fill();
  c.beginPath();
  c.moveTo(-8, 17);
  c.quadraticCurveTo(0, 25, 8, 17);
  inkStyle(c, 4.5);
  c.stroke();
  c.restore();
  c.restore();
  inkSparkle(c, 42, 52, 15, "#ffffff", { line: 4 });
}

/** Daily bonus: a cheerful tear-off calendar with binder rings and a smiling gold star. */
export function drawDailyIcon(c, w, h) {
  withInkDrop(c, w, h, 5.5, paintDaily);
}

// ---- Wheel ------------------------------------------------------------------------------

/** Bright segment colours (the in-game wheel uses its own muted set; the icon wants pop). */
var SEGMENTS = ["#ff5a5f", "#ff9a2e", "#ffd45c", "#5fd068", "#3fd0c8", "#4fa0ff", "#a985ff", "#ff7ac8"];

function paintWheel(c) {
  var cx = 128, cy = 144, R = 98, rim = 16, inner = R - rim;
  // Rim: cream ring with an ink edge outside and in.
  c.beginPath();
  c.arc(cx, cy, R, 0, TAU);
  c.fillStyle = "#fff4dc";
  c.fill();
  // Segments.
  var off = -Math.PI / 2 + Math.PI / 8;
  for (var i = 0; i < SEGMENTS.length; i++) {
    var a0 = off + (i / SEGMENTS.length) * TAU, a1 = off + ((i + 1) / SEGMENTS.length) * TAU;
    c.beginPath();
    c.moveTo(cx, cy);
    c.arc(cx, cy, inner, a0, a1);
    c.closePath();
    c.fillStyle = SEGMENTS[i];
    c.fill();
  }
  // Cel light and shade over the face: a lit cap upper left, a shade crescent lower right.
  c.save();
  c.beginPath();
  c.arc(cx, cy, inner, 0, TAU);
  c.clip();
  c.beginPath();
  c.arc(cx, cy, inner + 2, 0, TAU);
  c.arc(cx - 12, cy - 16, inner - 4, 0, TAU, true);
  c.fillStyle = "rgba(23,42,58,0.24)";
  c.fill();
  c.beginPath();
  c.ellipse(cx - 34, cy - 40, 52, 30, -0.75, 0, TAU);
  c.fillStyle = "rgba(255,255,255,0.28)";
  c.fill();
  c.restore();
  // Spokes between segments.
  c.beginPath();
  for (var s = 0; s < SEGMENTS.length; s++) {
    var a = off + (s / SEGMENTS.length) * TAU;
    c.moveTo(cx, cy);
    c.lineTo(cx + Math.cos(a) * inner, cy + Math.sin(a) * inner);
  }
  inkStyle(c, 5);
  c.stroke();
  c.beginPath();
  c.arc(cx, cy, inner, 0, TAU);
  inkStyle(c, ICON_DETAIL);
  c.stroke();
  // Rim bulbs.
  for (var b = 0; b < 8; b++) {
    var ba = off + Math.PI / 8 + (b / 8) * TAU;
    var bx = cx + Math.cos(ba) * (R - rim / 2), by = cy + Math.sin(ba) * (R - rim / 2);
    c.beginPath();
    c.arc(bx, by, 5.2, 0, TAU);
    c.fillStyle = b % 2 ? "#ffd45c" : "#ff8a3a";
    c.fill();
    inkStyle(c, 2.6);
    c.stroke();
  }
  // Rim shade on the lower right, then the outer ink.
  c.beginPath();
  c.arc(cx, cy, R - 3, 0.05, Math.PI * 0.62);
  c.strokeStyle = "rgba(201,170,120,0.9)";
  c.lineWidth = 5;
  c.stroke();
  c.beginPath();
  c.arc(cx, cy, R, 0, TAU);
  inkStyle(c, ICON_LINE);
  c.stroke();
  // Hub.
  celShape(c, function (cc, g) { cc.arc(cx, cy, 22 + g, 0, TAU); }, GOLD, 22, { line: ICON_DETAIL * 1.2 });
  glint(c, cx - 8, cy - 9, 4);
  // Pointer: a rounded red wedge at the top, tip down into the wheel.
  var px = cx, py = cy - R + 6;
  celShape(c, function (cc, g) {
    cc.moveTo(px, py + 22 + g * 1.4);
    cc.quadraticCurveTo(px - 4, py + 22 + g, px - 8 - g, py + 14);
    cc.lineTo(px - 26 - g, py - 22);
    cc.quadraticCurveTo(px - 30 - g, py - 36 - g, px - 16, py - 36 - g);
    cc.lineTo(px + 16, py - 36 - g);
    cc.quadraticCurveTo(px + 30 + g, py - 36 - g, px + 26 + g, py - 22);
    cc.lineTo(px + 8 + g, py + 14);
    cc.quadraticCurveTo(px + 4, py + 22 + g, px, py + 22 + g * 1.4);
    cc.closePath();
  }, { base: "#ff5a5f", light: "#ff9b9b", shade: "#d2363f" }, 30, {
    line: ICON_LINE * 0.9,
    lightPath: function (cc) { cc.ellipse(px - 14, py - 26, 10, 7, -0.3, 0, TAU); },
  });
  c.beginPath();
  c.arc(px, py - 16, 7, 0, TAU);
  c.fillStyle = "#ffd45c";
  c.fill();
  inkStyle(c, 4);
  c.stroke();
}

/** Wheel of fortune: bright segments, gold hub, bulbs on the rim and a red pointer on top. */
export function drawWheelIcon(c, w, h) {
  withInkDrop(c, w, h, 5.5, paintWheel);
}

// ---- Shrink aim cursor ------------------------------------------------------------------

var LIME = "#c6ff3a";

/**
 * Cursor for aiming the Shrink booster (32×32 at scale 1, hotspot 16,16): a lime ring with
 * four ticks and a centre dot, each laid over a wider ink stroke so it reads on the day sky,
 * the night sea and the white flask alike. Drawn at whole-pixel radii for a crisp edge.
 */
export function drawMinimizeAim(c, w, h) {
  var x = w / 2, y = h / 2;
  c.lineCap = "round";
  // Ink underlay.
  c.strokeStyle = INK;
  c.lineWidth = 5;
  c.beginPath();
  c.arc(x, y, 9, 0, TAU);
  c.stroke();
  c.lineWidth = 4.6;
  c.beginPath();
  c.moveTo(x, 2.5); c.lineTo(x, 7);
  c.moveTo(x, h - 2.5); c.lineTo(x, h - 7);
  c.moveTo(2.5, y); c.lineTo(7, y);
  c.moveTo(w - 2.5, y); c.lineTo(w - 7, y);
  c.stroke();
  c.fillStyle = INK;
  c.beginPath();
  c.arc(x, y, 3.2, 0, TAU);
  c.fill();
  // Lime on top.
  c.strokeStyle = LIME;
  c.lineWidth = 2.2;
  c.beginPath();
  c.arc(x, y, 9, 0, TAU);
  c.stroke();
  c.lineWidth = 2;
  c.beginPath();
  c.moveTo(x, 2.5); c.lineTo(x, 7);
  c.moveTo(x, h - 2.5); c.lineTo(x, h - 7);
  c.moveTo(2.5, y); c.lineTo(7, y);
  c.moveTo(w - 2.5, y); c.lineTo(w - 7, y);
  c.stroke();
  c.fillStyle = LIME;
  c.beginPath();
  c.arc(x, y, 1.7, 0, TAU);
  c.fill();
}

